import { Button, ButtonWithPopup, SpinningLoader, Text } from '@innovationdepartment/proxima-ui';
import { useState } from 'react';
import { PROMPT_MAX_LENGTH } from '../lib/constants';
import { pickFilters } from '../lib/filters';
import type { PreviewState } from '../store/wizardTypes';
import type { ClarifyPreviewResponse, DefinitionPreviewResponse, ValidationError } from '../types';
import FilterSummary from './FilterSummary/FilterSummary';
import {
  Actions,
  Counter,
  GeneratorCard,
  GeneratorDescription,
  GeneratorPromptBox,
  GeneratorPromptTextarea,
  GeneratorTitle,
  InlineErrors,
  InterpretationHeading,
  InterpretationList,
  InterpretationSection,
  InterpretationText,
  InterpretationUnmappedLabel,
  Notice,
  PromptFooter,
  Questions,
  Thinking
} from './form.styles';
import { ConfirmActions, ConfirmContent, PrimaryButton } from './shared.styles';

const PLACEHOLDER = [
  'Women 25 to 45 in California and New York who buy skincare serums',
  'Repeat buyers of premium cookware brands in higher-income zip codes',
  'Men 50+ who bought in the last year, top 100k by lifetime value'
].join('\n\n');

type NlGeneratorProps = {
  prompt: string;
  preview: PreviewState;
  acceptedPreview: DefinitionPreviewResponse | null;
  /** Whether the draft holds rules, a name, or a description that accepting a new preview would replace. */
  hasDraft: boolean;
  promptStale: boolean;
  onPromptChange: (prompt: string) => void;
  onSubmit: () => void;
  onAccept: () => void;
};

/** Right sidebar: prompt in, previewed audience out; accepting loads it into the rules. */
const NlGenerator = (props: NlGeneratorProps) => {
  const { prompt, preview } = props;
  const previewing = preview.kind === 'previewing';
  const validationErrors: ValidationError[] =
    preview.kind === 'error' ? preview.validationErrors : [];
  return (
    <>
      <GeneratorPromptBox>
        <GeneratorPromptTextarea
          aria-label="Audience description"
          placeholder={PLACEHOLDER}
          value={prompt}
          maxLength={PROMPT_MAX_LENGTH}
          onChange={event => props.onPromptChange(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              props.onSubmit();
            }
          }}
        />
        <Counter near={prompt.length > PROMPT_MAX_LENGTH * 0.9}>
          {prompt.length.toLocaleString('en-US')} / {PROMPT_MAX_LENGTH.toLocaleString('en-US')}
        </Counter>
      </GeneratorPromptBox>
      {validationErrors.length > 0 ? (
        <InlineErrors>
          {validationErrors.map(error => (
            <li key={`${error.path}-${error.message}`}>{error.message}</li>
          ))}
        </InlineErrors>
      ) : null}
      {preview.kind === 'error' ? (
        <div role="alert">
          <Text variant="body3" color="red60">
            {preview.message}
          </Text>
        </div>
      ) : null}
      {props.promptStale && preview.kind === 'definition' ? (
        <Text variant="body3" color="orange60">
          Prompt changed. Submit again to refresh the preview.
        </Text>
      ) : null}
      <PromptFooter>
        <PrimaryButton
          label={previewing ? 'Interpreting…' : 'Generate'}
          size="small"
          disabled={previewing || !prompt.trim()}
          onClick={props.onSubmit}
        />
      </PromptFooter>
      <PreviewResult {...props} />
    </>
  );
};

const PreviewResult = (props: NlGeneratorProps) => {
  const { preview } = props;
  switch (preview.kind) {
    case 'idle':
      return null;
    case 'previewing':
      return (
        <Thinking>
          <SpinningLoader />
          Interpreting… this can take up to a minute.
        </Thinking>
      );
    case 'error':
      /* The error shows under the prompt box */
      return null;
    case 'clarify':
      return <ClarifyPanel preview={preview.preview} />;
    case 'decline':
      return (
        <GeneratorCard>
          <GeneratorTitle>This can't be built as one audience</GeneratorTitle>
          <Unmapped items={preview.preview.unmapped} tone="error" />
        </GeneratorCard>
      );
    default: {
      const response = preview.preview;
      const loaded = props.acceptedPreview === response;
      return (
        <GeneratorCard>
          <GeneratorTitle>{response.name ?? 'Untitled audience'}</GeneratorTitle>
          {response.description ? (
            <GeneratorDescription>{response.description}</GeneratorDescription>
          ) : null}
          <FilterSummary filters={pickFilters(response)} layout="strip" />
          <Interpretation rationale={response.rationale} unmapped={response.unmapped} />
          <Actions>
            {loaded ? (
              <Button label="Reload" size="small" variant="outlined" onClick={props.onAccept} />
            ) : (
              <UseSettingsButton confirm={props.hasDraft} onAccept={props.onAccept} />
            )}
          </Actions>
        </GeneratorCard>
      );
    }
  }
};

const ClarifyPanel = ({ preview }: { preview: ClarifyPreviewResponse }) => (
  <GeneratorCard>
    <GeneratorTitle>A few questions first</GeneratorTitle>
    <Questions>
      {preview.clarifications.map(question => (
        <li key={question}>{question}</li>
      ))}
    </Questions>
    <Unmapped items={preview.unmapped} tone="warning" />
    <GeneratorDescription>
      Add these details to the prompt above and generate again.
    </GeneratorDescription>
  </GeneratorCard>
);

/**
 * How the service read a definition prompt, then what it could not express.
 *
 * Renders nothing when there is neither a rationale nor an unmapped item.
 */
const Interpretation = ({ rationale, unmapped }: { rationale: string; unmapped: string[] }) => {
  if (!rationale && unmapped.length === 0) return null;
  return (
    <InterpretationSection>
      <InterpretationHeading>Interpretation</InterpretationHeading>
      {rationale ? <InterpretationText>{rationale}</InterpretationText> : null}
      {unmapped.length > 0 ? (
        <>
          <InterpretationUnmappedLabel>Could not express:</InterpretationUnmappedLabel>
          <InterpretationList>
            {unmapped.map(item => (
              <li key={item}>{item}</li>
            ))}
          </InterpretationList>
        </>
      ) : null}
    </InterpretationSection>
  );
};

/**
 * Loads a preview that is not loaded yet.
 *
 * With a draft to replace, it asks first; with an empty draft, it applies at once.
 */
const UseSettingsButton = ({ confirm, onAccept }: { confirm: boolean; onAccept: () => void }) => {
  const [open, setOpen] = useState(false);
  const button = (onClick: () => void) => (
    <Button label="Use these settings" size="small" variant="contained" onClick={onClick} />
  );
  if (!confirm) return button(onAccept);
  return (
    <ButtonWithPopup
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-right"
      /* ButtonWithPopup opens from its own wrapper, so the trigger needs no handler */
      buttonElement={button(() => {})}
    >
      <ConfirmContent>
        <Text variant="body2">Replace the current rules, name, and description?</Text>
        <ConfirmActions>
          <Button label="Keep mine" variant="text" size="small" onClick={() => setOpen(false)} />
          <Button
            label="Replace"
            size="small"
            onClick={() => {
              setOpen(false);
              onAccept();
            }}
          />
        </ConfirmActions>
      </ConfirmContent>
    </ButtonWithPopup>
  );
};

const Unmapped = ({ items, tone }: { items: string[]; tone: 'warning' | 'error' }) =>
  items.length > 0 ? (
    <Notice tone={tone}>
      <strong>{tone === 'warning' ? 'Could not express:' : 'Why not:'}</strong>
      <ul>
        {items.map(item => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Notice>
  ) : null;

export default NlGenerator;
